import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TourDetailService } from '../../../services/resources/tour-detail.service';
import { TourDetailInterface } from '../../../core/models';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs';
import { Timestamp } from '@angular/fire/firestore';
import { ColorPickerDirective } from 'ngx-color-picker';

@Component({
  selector: 'app-tours-editor',
  imports: [CommonModule, ReactiveFormsModule, ColorPickerDirective],
  standalone: true,
  template: `
    <div class="mx-auto max-w-7xl pt-6 px-4 sm:px-6 lg:px-8">
      <form [formGroup]="tourForm" (ngSubmit)="onSubmit()" class="space-y-12">
        <div class="border-b border-gray-900/10 pb-12">
          <h2 class="text-base font-semibold leading-7 text-gray-900">
            Tour Detail
          </h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">
            Enter the details for the tour below.
          </p>

          <div class="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <!-- Hidden ID field -->
            <input type="hidden" formControlName="id" />

            <!-- Tour Name -->
            <div class="sm:col-span-4">
              <label
                for="tourName"
                class="block text-sm font-medium leading-6 text-gray-900"
              >
                Tour Name
              </label>
              <input
                id="tourName"
                type="text"
                formControlName="tourName"
                placeholder="e.g. Vienna Art Walk"
                class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-columbus-blue-600 sm:text-sm sm:leading-6"
              />
              <div
                *ngIf="
                  tourForm.get('tourName')?.invalid &&
                  tourForm.get('tourName')?.touched
                "
                class="mt-1 text-sm text-red-600"
              >
                Tour name is required.
              </div>
            </div>

            <!-- Color Code -->
            <div class="sm:col-span-2">
              <label
                for="colorCode"
                class="block text-sm font-medium leading-6 text-gray-900"
              >
                Color Code
              </label>
              <input
                autocomplete="off"
                id="colorCode"
                formControlName="colorCode"
                [colorPicker]="colorCode"
                (colorPickerChange)="changeColor($event)"
                [cpPosition]="'bottom'"
                [cpOutputFormat]="'hex'"
                [cpAlphaChannel]="'forced'"
                [style.background]="colorCode"
                class="mt-2 h-10 w-full rounded-md border-0 p-0 shadow-sm
         ring-1 ring-inset ring-gray-300 focus:ring-2
         focus:ring-inset focus:ring-columbus-blue-600 text-transparent"
              />
            </div>
          </div>
        </div>

        <div class="mt-6 flex items-center justify-end gap-x-6">
          <button
            type="button"
            (click)="onCancel()"
            class="text-sm font-semibold leading-6 text-gray-900"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="tourForm.invalid"
            class="rounded-md bg-columbus-blue-600 disabled:opacity-25 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  `,
  styles: ``,
})
export class ToursEditorComponent {
  private tourService = inject(TourDetailService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  /** Firestore-tour document ID */
  private tourId: string | null = null;

  tourForm = this.fb.group({
    /** This is the nested detail‐ID */
    id: [''],
    tourName: ['', Validators.required],
    createdDate: Timestamp.now(),
    lastEditDate: Timestamp.now(),
    colorCode: ['#ffffff00', Validators.pattern(/^#[0-9A-Fa-f]{8}$/)],
  });

  constructor() {
    this.tourId = this.route.snapshot.paramMap.get('id');

    if (!this.tourId) {
      return;
    }

    this.tourService
      .getTourData(this.tourId)
      .pipe(first())
      .subscribe((tour: TourDetailInterface) => {
        if (tour) {
          this.patchForm(tour);
        }
      });
  }

  get colorCode(): string {
    return this.tourForm.get('colorCode')?.value as string;
  }
  changeColor(val: string) {
    this.tourForm.get('colorCode')?.setValue(val);
  }

  private patchForm(detail: TourDetailInterface): void {
    this.tourForm.patchValue({
      id: detail.id,
      tourName: detail.tourName,
      colorCode: detail.colorCode ?? '',
      createdDate: detail.createdDate,
      lastEditDate: detail.lastEditDate,
    });
  }

  async onSubmit(): Promise<void> {
    if (this.tourForm.invalid) {
      return;
    }

    const tour = this.tourForm.getRawValue() as TourDetailInterface;

    if (this.tourId) {
      this.tourService.updateTour(tour);
    } else {
      this.tourService.createNewTour(tour);
    }
    this.router.navigateByUrl('/tours');
  }

  onCancel(): void {
    this.router.navigateByUrl('/tours');
  }
}
